import { companyAvatar, fileAvatar } from 'Services/avatarGenerator'
import { default as locale } from 'date-fns__legacy__/locale/pt'
import format from 'date-fns__legacy__/format'
import parse from 'date-fns__legacy__/parse'
import quotationDeadline from 'Enums/quotationDeadline'
import { ApiQuoteRequestFile, QuoteFile, ApiQuoteRequestDetail, QuoteRequestDetail, ApiQuoteRequestPreview, QuoteRequestPreview, ApiQuoteLastBudgetPreview, quoteLastBudgetPreview, ApiQuoteLastestActivities, quoteLastestActivities, ApiQuoteLastMessages, quoteLastMessages, ApiCategoryQuotationRequest } from 'Types'
import { truncateFilename, getFileExtension } from 'Services/fileService'
import { tr } from 'date-fns/locale'

const parseForm = (formItem) => {
  const { formItemId, question, answer, inputTypeTag } = formItem
  const type = (inputTypeTag + '').toUpperCase()

  return { formItemId, question, answer, type }
}

const parseFile = (item: ApiQuoteRequestFile): QuoteFile => {
  const fileExtension = getFileExtension(item.fileName)

  return {
    isImage: item.isImage,
    title: truncateFilename(item.fileName),
    url: item.isImage ? item.imageLargeUrl : item.fileUrl,
    thumbnail: item.isImage ? item.imageSmallUrl : fileAvatar(fileExtension),
    width: item.imageLargeWidth,
    height: item.imageLargeHeight,
  }
}

export const parseRequestPreview = (item: ApiQuoteRequestPreview): QuoteRequestPreview => {

  return {
    id: item.id,
    category: item.category,
    serviceType: item.serviceType,
    token: item.identifier,
    dateCreated: parse(item.dateCreated),
    created: format(parse(item.dateCreated), 'DD/MM/YYYY [às] HH:mm', { locale }),
    statusEnum: item.statusEnum,
    newMessages: 0,
    budgets: 0,
    participants: 0,
    readyForReview: false,
    hasReview: false,
    senderName: item.senderName,
    urlRequotation: item.urlRequotation,
    interestedCompanies: item.interestedCompanies,
    hasFailed: item.hasFailed,
    totalUnreadMessages: item.totalUnreadMessages,
    totalEstimateAmount: item.totalEstimateAmount,
    totalUnreadEvaluations: item.totalUnreadEvaluations,
    address: {
      name: item.address.description,
      street: item.address.street,
      number: item.address.streetNumber,
      complementary: item.address.complementary,
      neighborhood: item.address.neighborhood,
      state: item.address.state,
      city: item.address.city,
      zipcode: item.address.zipcode,
    }
  }
}


export const parseLastBudget = (item: ApiQuoteLastBudgetPreview): quoteLastBudgetPreview => {

  return {
    id: item.id,
    category: item.category,
    serviceType: item.serviceType,
    token: item.identifier,
    dateCreated: parse(item.dateCreated),
    created: format(parse(item.dateCreated), 'DD/MM/YYYY [às] HH:mm', { locale }),
    statusEnum: item.statusEnum,
    readyForReview: false,
    hasReview: false,
    senderName: item.senderName,
    urlRequotation: item.urlRequotation,
    hasFailed: item.hasFailed,
    address: {
      name: item.address.description,
      street: item.address.street,
      number: item.address.streetNumber,
      complementary: item.address.complementary,
      neighborhood: item.address.neighborhood,
      state: item.address.state,
      city: item.address.city,
      zipcode: item.address.zipcode,
    },
    lastBudget: item.lastBudget
  }
}

export const parseLastestActivities = (item: ApiQuoteLastestActivities): quoteLastestActivities => {

  return {
    id: item.id,
    serviceType: item.serviceType,
    identifier: item.identifier,
    dateCreated: parse(item.dateCreated),
    created: format(parse(item.dateCreated), 'DD/MM/YYYY [às] HH:mm', { locale }),
    address: {
      name: item.address.description,
    },
    lastActivity: item.lastActivity
  }
}

export const parseLastMessages = (item: ApiQuoteLastMessages): quoteLastMessages => {
  return { 
    id: item.id,
    description: item.description,
    dateCreated: parse(item.dateCreated),
    category: item.category,
    serviceType: item.serviceType,
    address: {
      description: item.address.description,
      street: item.address.street,
      streetNumber: item.address.streetNumber,
      complementary: item.address.complementary,
      neighborhood: item.address.neighborhood,
      state: item.address.state,
      city: item.address.city,
      zipcode: item.address.zipcode,
    },
    identifier: item.identifier,
    statusDescription: item.statusDescription,
    statusEnum: item.statusEnum,
    statusQuotation: item.statusQuotation,
    senderName: item.senderName,
    urlRequotation: item.urlRequotation,
    lastMessages: {
      id: item.lastMessages.id,
      tag: item.lastMessages.tag,
      description: item.lastMessages.description,
      dateCreated: item.lastMessages.dateCreated,
      dateCreatedFormat: format(parse(item.lastMessages.dateCreated), 'DD/MM/YYYY [às] HH:mm', { locale }),
      companyName: item.lastMessages.companyName,
    }
  }
}

/**
 * @param {ApiQuoteRequestStatus} status
 * @returns {(requestPreview: QuoteRequestPreview) => QuoteRequestPreview}
 */
export const parseRequestPreviewStatus = (status) => (requestPreview) => {

  const requestStatus = {
    budgets: status.budgetReceived,
    hasFailed: status.hasFailed,
    newMessages: status.unreadMessages,
    participants: status.companyAttendant,
    hasReview: status.hasEvaluation,
    readyForReview: status.availableToEvaluate,
  }

  return Object.assign({}, requestPreview, requestStatus)
}

export const parseRequestDetails = (item: ApiQuoteRequestDetail): QuoteRequestDetail => {
  return {
    id: item.id,
    token: item.identifier,
    category: item.category,
    serviceTypeId: item.serviceTypeId,
    serviceType: item.serviceType,
    created: format(parse(item.dateCreated), 'DD/MM/YYYY [às] HH:mm', { locale }),
    interestedCompanies: item.interestedCompanies,
    fileList: item.files.map(parseFile),
    description: item.description,
    phoneDDD: item.phoneDDD,
    phoneNumber: item.phoneNumber,
    formId: item.formId,

    user: {
      id: item.user.id,
      name: item.user.name,
      email: item.user.email,
      photo: item.user.photo,
      contactPreferenceDescription: item.user.contactPreferenceDescription,
      contactPreference: item.user.contactPreference
    },

    request: {
      daysLeftForEvaluation: item.daysLeftEvaluated,
      availableForReview: item.canEvaluate,
      questionList: item.answers.map(parseForm),
      details: item.description,
      deadline: quotationDeadline.getDescriptionByValue(item.periodStartOfService),
      attachments: item.files.length,
      status: item.quotationStatus,
      statusText: item.quotationStatusDescription,
    },

    address: {
      id: item.address.id,
      street: item.address.street,
      number: item.address.streetNumber,
      details: item.address.complementary,
      neighborhood: item.address.neighborhood,
      zipcode: item.address.zipcode,
      city: item.address.city,
      state: item.address.state,
      name: item.address.description,
    },
  }
}

/**
 * @param {ApiQuoteRequestReview} item
 * @returns {QuoteRequestReview}
 */
export const parseRequestReview = (item) => ({
  reviewId: item.id,
  negotiationId: item.idCompanyQuotation,
  rating: item.rating,
  comment: item.comment || '',
  lastUpdated: '',
  company: {
    logo: companyAvatar(item.companyLogoPath, item.companyName),
    name: item.companyName,
    reply: item.reply || '',
  }
})

/**
 * Parser before redo quotation
 * @param store item
 * @returns {ApiCategoryQuotationRequest}
 */
export const redoQuoteParser = (quote) : ApiCategoryQuotationRequest => {
  return {
   description: quote.description,
   origin: 4,
   type: 2,
   periodStartOfService: 1,
   allowAddCompanies: true,
   serviceTypeId: quote.serviceTypeId,
   addressId: quote.address.id,
   ddd: quote.phoneDDD,
   phoneNumber: quote.phoneNumber,
   formId: quote.formId,
   companyIds: [],
   files:[],
   answers: quote.request.questionList.map(x => {
    return {
      formItemId: x.formItemId,
      answer: x.answer     
    }
   }),
   receiveSponsorship: false,
   crossQuotServiceTypeIds: [],
   contactPreference: 0
  }
}



export default {
  parseRequestDetails,
}